import { ApolloError } from '@apollo/client';
import { t } from '../../../../i18n';

const WORKFLOW_MESSAGE_KEY_PREFIX = 'errors.business.workflow.';

function getGraphQLError(error: unknown) {
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0];
  }
  return null;
}

function translateMessageKey(messageKey: string, details?: Record<string, unknown>): string | null {
  const translated = t(messageKey, details);
  if (typeof translated === 'string' && translated !== messageKey) {
    return translated;
  }
  return null;
}

function extractMessageKeyFromRaw(raw: string): string | null {
  const match = raw.match(/errors\.business\.workflow\.\w+/);
  return match?.[0] ?? null;
}

/**
 * 将审批相关 GraphQL 错误转为可读中文提示（优先 i18n messageKey，其次后端 lazyMessage）。
 */
export function resolveWorkflowErrorMessage(
  error: unknown,
  fallback = '操作失败，请稍后重试',
): string {
  const gqlError = getGraphQLError(error);
  if (!gqlError) {
    if (error instanceof Error && error.message && !error.message.includes('errors.business.')) {
      return error.message;
    }
    return fallback;
  }

  const extensions = gqlError.extensions ?? {};
  const messageKey = extensions.messageKey as string | undefined;
  const backendMessage = extensions.message as string | undefined;
  const details = (extensions.details as Record<string, unknown>) ?? {};

  if (
    backendMessage &&
    !backendMessage.includes('errors.business.') &&
    !backendMessage.startsWith('[BUSINESS_ERROR]') &&
    backendMessage !== 'Validation error'
  ) {
    return backendMessage;
  }

  if (messageKey) {
    const translated = translateMessageKey(messageKey, details);
    if (translated) {
      return translated;
    }
  }

  const rawMessage = gqlError.message ?? '';
  const keyFromRaw = extractMessageKeyFromRaw(rawMessage);
  if (keyFromRaw) {
    const translated = translateMessageKey(keyFromRaw, details);
    if (translated) {
      return translated;
    }
  }

  return fallback;
}

export function isWorkflowActiveInstanceError(error: unknown): boolean {
  const gqlError = getGraphQLError(error);
  if (!gqlError) {
    return false;
  }
  const messageKey = gqlError.extensions?.messageKey as string | undefined;
  if (messageKey === `${WORKFLOW_MESSAGE_KEY_PREFIX}activeInstanceExists`) {
    return true;
  }
  const raw = gqlError.message ?? '';
  return raw.includes(`${WORKFLOW_MESSAGE_KEY_PREFIX}activeInstanceExists`);
}

/** 审批已完成/节点已处理等需刷新页面的场景 */
export function isWorkflowStaleStateError(error: unknown): boolean {
  const gqlError = getGraphQLError(error);
  if (!gqlError) {
    return false;
  }
  const staleKeys = new Set([
    `${WORKFLOW_MESSAGE_KEY_PREFIX}instanceNotActive`,
    `${WORKFLOW_MESSAGE_KEY_PREFIX}noCurrentNode`,
    `${WORKFLOW_MESSAGE_KEY_PREFIX}nodeAlreadyHandled`,
    `${WORKFLOW_MESSAGE_KEY_PREFIX}nodeNotCurrent`,
  ]);
  const messageKey = gqlError.extensions?.messageKey as string | undefined;
  if (messageKey && staleKeys.has(messageKey)) {
    return true;
  }
  const raw = gqlError.message ?? '';
  return Array.from(staleKeys).some((key) => raw.includes(key));
}
